import { SearchOutlined } from '@ant-design/icons';
import { Button, Dropdown, Input, Skeleton, Tooltip } from 'antd';
import cn from 'classnames';
import { useMemo, useState } from 'react';

import { getRecommendChatReason } from '@app/api/RecommendChat/errors';
import { type LeadCardIntegrationItemData } from '@app/components/LeadCard/data';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';

import type { FC } from 'react';

import './IntegrationSelector.scss';

interface IntegrationSelectorProps {
  integrations: LeadCardIntegrationItemData[];
  selectedIntegration: LeadCardIntegrationItemData | null;
  isLoading?: boolean;
  onIntegrationSelect(integration: LeadCardIntegrationItemData): void;
}

export const IntegrationSelector: FC<IntegrationSelectorProps> = ({
  integrations,
  selectedIntegration,
  isLoading = false,
  onIntegrationSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const hasIntegrations = integrations.length > 0;

  const filteredIntegrations = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return integrations;

    return integrations.filter(({ name, branchName }) =>
      `${name} ${branchName ?? ''}`.toLowerCase().includes(normalizedSearch)
    );
  }, [integrations, search]);

  const openChangeHandler = (nextOpen: boolean) => {
    setIsOpen(nextOpen);

    if (!nextOpen) setSearch('');
  };

  const selectHandler = (integration: LeadCardIntegrationItemData) => {
    onIntegrationSelect(integration);
    setIsOpen(false);
    setSearch('');
  };

  const dropdownContent = (
    <div className="IntegrationSelector__dropdown">
      <Input
        className="IntegrationSelector__search"
        size="small"
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Поиск интеграции"
        value={search}
        onChange={({ target: { value } }) => setSearch(value)}
      />

      {filteredIntegrations.length > 0 ? (
        <ul className="IntegrationSelector__list">
          {filteredIntegrations.map((integration) => (
            <li key={String(integration.id)}>
              <Tooltip
                title={
                  integration.recommendChat?.available === false
                    ? getRecommendChatReason(
                        integration.recommendChat.reasonCode
                      )
                    : undefined
                }
              >
                <button
                  type="button"
                  className={cn('IntegrationSelector__option', {
                    'IntegrationSelector__option--selected':
                      integration.id === selectedIntegration?.id,
                    'IntegrationSelector__option--unavailable':
                      integration.recommendChat?.available === false,
                  })}
                  onClick={() => selectHandler(integration)}
                >
                  <SocialIntegrationIcon
                    type={integration.type}
                    size="sm"
                    className="IntegrationSelector__option-icon"
                  />
                  <span className="IntegrationSelector__option-text">
                    <span className="IntegrationSelector__option-name">
                      {integration.name}
                    </span>
                    {integration.branchName && (
                      <span className="IntegrationSelector__option-branch">
                        {integration.branchName}
                      </span>
                    )}
                    {integration.recommendChat?.available === false && (
                      <span className="IntegrationSelector__option-branch">
                        Недоступен
                      </span>
                    )}
                  </span>
                </button>
              </Tooltip>
            </li>
          ))}
        </ul>
      ) : (
        <div className="IntegrationSelector__empty">Ничего не найдено</div>
      )}
    </div>
  );

  if (!isLoading && !(hasIntegrations && selectedIntegration)) {
    // No active integration: show only plain grey text, without the
    // "Отправить от" label or a selector-like button, since there is
    // nothing to select or send through. Sending is already disabled.
    return (
      <div className="IntegrationSelector">
        <span className="IntegrationSelector__no-integration">
          Нет активной интеграции
        </span>
      </div>
    );
  }

  return (
    <div className="IntegrationSelector">
      {isLoading || !selectedIntegration ? (
        <Skeleton.Input
          active
          className="IntegrationSelector__skeleton"
          size="small"
        />
      ) : (
        <Dropdown
          trigger={['click']}
          open={isOpen}
          onOpenChange={openChangeHandler}
          popupRender={() => dropdownContent}
        >
          <Button className="IntegrationSelector__trigger" type="link">
            <SocialIntegrationIcon type={selectedIntegration.type} size="sm" />
            <span className="IntegrationSelector__trigger-text">
              {selectedIntegration.name}
            </span>
          </Button>
        </Dropdown>
      )}
    </div>
  );
};
